import type { AdminScopeCounts } from '@steado/admin-contract/scope-counts';

import type { UsersFilters } from './users-query';

/**
 * The design's scope row, as filter presets over the one endpoint.
 *
 * Presets rather than tabs: every one of these is the same list under
 * different `where` clauses, and a tab backed by a different endpoint would
 * quietly break the shared toolbar and pager underneath.
 *
 * The counts come from `/admin/scope-counts`, not from the list: cursor
 * pagination never issues a COUNT, so no page of users knows how many there
 * are. `countKey` names which field of that response each pill reads, and a
 * pill renders without a number when the answer has not arrived.
 */
export const USER_SCOPES = [
  { id: 'all', label: 'Everyone', countKey: 'all', filters: {} },
  { id: 'failed', label: 'Plans failing', countKey: 'plansFailing', filters: { plan: 'failed' } },
  { id: 'hold', label: 'Safety hold', countKey: 'safetyHold', filters: { plan: 'safety_hold' } },
  { id: 'generating', label: 'Generating now', countKey: 'generating', filters: { plan: 'generating' } },
  { id: 'incomplete', label: 'Never onboarded', countKey: 'neverOnboarded', filters: { onboarded: 'no' } },
  { id: 'deactivated', label: 'Deactivated', countKey: 'deactivated', filters: { status: 'deactivated' } },
] as const satisfies readonly {
  readonly id: string;
  readonly label: string;
  /** Which field of `scope-counts`' users block this pill's number comes from. */
  readonly countKey: keyof AdminScopeCounts['users'];
  readonly filters: Partial<UsersFilters>;
}[];

export type UserScopeId = (typeof USER_SCOPES)[number]['id'];

/**
 * Which pill the current filters amount to.
 *
 * Derived from the filters rather than stored beside them, so a hand-edited
 * URL and a clicked pill cannot disagree about which scope is lit. Anything
 * that matches no preset lights "Everyone", which is what the toolbar filters
 * are then narrowing.
 */
export function scopeForFilters(filters: UsersFilters): UserScopeId {
  if (filters.status === 'deactivated') return 'deactivated';
  if (filters.onboarded === 'no') return 'incomplete';
  if (filters.plan === 'failed') return 'failed';
  if (filters.plan === 'safety_hold') return 'hold';
  if (filters.plan === 'generating') return 'generating';
  return 'all';
}

/** The filters a pill sets, with every other scope's filters cleared. */
export function filtersForScope(id: UserScopeId, current: UsersFilters): UsersFilters {
  const scope = USER_SCOPES.find((candidate) => candidate.id === id);

  return {
    // The search box, the diet, platform and gym dropdowns survive a scope change: they
    // are the operator's own narrowing, not part of the scope.
    ...(current.search === undefined ? {} : { search: current.search }),
    ...(current.diet === undefined ? {} : { diet: current.diet }),
    ...(current.platform === undefined ? {} : { platform: current.platform }),
    ...(current.gym === undefined ? {} : { gym: current.gym }),
    ...(scope?.filters ?? {}),
  };
}
